<template>
  <div ref="line" class="sheet"></div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'isp-line',
  props: {
    lineData: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    maxLineDataValue() {
      // 遍历 lineData 数组，过滤每个对象中的 'time' 键
      const maxValue = this.lineData.reduce((max, item) => {
        // 过滤掉每个对象中的 'time' 键
        const filteredItem = Object.keys(item)
          .filter((key) => key !== 'time') // 过滤掉 'time' 键
          .reduce((obj, key) => {
            obj[key] = item[key]; // 将过滤后的键值重新组合成对象
            return obj;
          }, {});

        // 找到当前对象的最大值
        const currentMax = Math.max(...Object.values(filteredItem)); // 获取当前对象的最大值
        return currentMax > max ? currentMax : max; // 返回更大的值
      }, 0); // 初始最大值为 0

      // 如果最大值是 0，则返回 1，否则向上取整到最近的 10 的倍数
      // return maxValue === 0 ? 1 : Math.ceil(maxValue / 10) * 10;
      return maxValue === 0 ? 1 : Math.ceil(maxValue / 10 ** Math.floor(Math.log10(maxValue))) * 10 ** Math.floor(Math.log10(maxValue));
    },
    maxLineDataKey() {
      let maxKey = null;
      let maxValue = -Infinity;
      // 遍历所有对象
      this.lineData.forEach((item) => {
        // 遍历当前对象的键，排除 'time' 键
        Object.keys(item).forEach((key) => {
          if (key !== 'time' && item[key] > maxValue) {
            maxValue = item[key]; // 更新最大值
            maxKey = key; // 更新最大值对应的键
          }
        });
      });
      return maxKey; // 返回最大的键
    },
    maxLineDataEvent() {
      return 0;
    },
    maxLineDataDevice() {
      return 0;
    },
  },
  data() {
    return {
      lineProps: [],
      myChart: null,
      title: '',
      unit: '',
    };
  },
  watch: {
    lineData() {
      this.drawLine();
    }
  },
  mounted: function () {
    this.drawLine();
  },
  methods: {
    initProps() {
      this.unit = this.$t('dms_chart_line.tai');
      this.lineProps = [
        {
          name: 'US_hanxun',
          code: 'us_hanxun',
          color: '#202945',
          type: 'line',
        },
        {
          name: 'EU-hanxun',
          code: 'eu_hanxun',
          color: '#93DA49',
          type: 'line',
        },
        {
          name: 'CN-hanxun',
          code: 'cn_hanxun',
          color: '#EB5A5A',
          type: 'line',
        },
        {
          name: 'APAC-hanxun',
          code: 'apac_hanxun',
          color: '#f6c97c',
          type: 'line',
        },
        {
          name: 'US-Vodafone',
          code: 'us_vodafone',
          color: '#93bff9',
          type: 'line',
        },
        {
          name: 'EU-Vodafone',
          code: 'eu_vodafone',
          color: '#8500ff',
          type: 'line',
        },
        {
          name: 'CN-Vodafone',
          code: 'cn_vodafone',
          color: '#f4a5ad',
          type: 'line',
        },
        {
          name: 'APAC-Vodafone',
          code: 'apac_vodafone',
          color: '#00A0D9',
          type: 'line',
        },
      ];
    },

    drawLine() {
      this.initProps();
      if (this.myChart) {
        this.myChart.dispose();
      }
      // 基于准备好的dom，初始化echarts实例
      this.myChart = echarts.init(this.$refs.line);
      window.addEventListener('resize', () => {
        this.myChart.resize();
      });
      // 绘制图表
      this.myChart.setOption({
        tooltip: {
          axisPointer: {
            type: 'none',
          },
          trigger: 'axis',
          show: true,
          padding: null,
          textStyle: null,
          position: null,
          formatter: null,
        },
        title: {
          text: this.title,
          left: 0,
          top: 0,
          textStyle: {
            fontSize: 12,
            lineHeight: 32,
            color: '#4D4D4D',
            fontWeight: 'bold',
          },
        },
        graphic: [],
        grid: {
          left: 50,
          right: 10,
          top: 56, // y1表示dataZoom与Y轴的距离
          bottom: 40, // y2表示dataZoom与X轴的距离
        },
        legend: {
          top: 2,
          textStyle: {
            color: '#1A1A1A',
            fontSize: 12,
            lineHeight: 12,
          },
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          axisLine: {
            show: true,
            lineStyle: { color: '#E6E6E6' },
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: true,
            margin: 10,
            fontSize: 12,
            fontWeight: 400,
            color: '#4D4D4D',
          },
          axisPointer: {
            show: true,
            type: 'none',
          },
          data: this.lineData.map((t) => t.month),
        },

        yAxis: this.lineProps.map((t) => ({
          type: 'value',
          scale: true,
          show: t.code === this.maxLineDataKey && true,
          name: this.unit,
          position: 'left',
          nameGap: 10,
          nameTruncate: {
            maxWidth: 90,
            ellipsis: '...',
          },
          nameTextStyle: {
            color: '#818181',
            fontSize: 12,
            lineHeight: 20,
            fontWeight: 400,
            padding: [0, 40, 0, 0],
          },
          axisLine: {
            show: false,
            lineStyle: { color: '#EEEEEE' },
          },
          axisLabel: {
            margin: 10,
            fontSize: 12,
            fontWeight: 400,
            color: '#4D4D4D',
            formatter: (value) => {
              // 判断是否为整数并需要格式化
              if (value >= 1000) {
                const formattedValue = (value / 1000).toFixed(1);
                return formattedValue.endsWith('.0') ? `${parseInt(formattedValue, 10)}k` : `${formattedValue}k`;
              } else {
                return value;
              }
            },
          },
          splitLine: {
            show: t.code === this.maxLineDataKey && true,
            lineStyle: { color: '#EEEEEE' },
          },
          minInterval: 1,
          min: 0,
          max: this.maxLineDataValue,
        })),
        series: this.lineProps.map((t, i) => ({
          name: t.name,
          data: this.lineData.map((f) => f[t.code]),
          type: t.type,
          yAxisIndex: i,
          barWidth: t.type === 'bar' ? '17' : null,
          lineStyle: {
            color: t.color,
            with: 3,
          },
          symbol: 'circle',
          symbolSize: 8,
          legendHoverLink: false,
          itemStyle: {
            color: t.color,
            borderColor: '#ffffff',
            borderWidth: 2,
            borderType: 'solid',
          },
        })),
      });
    },
  },
};
</script>

<style lang="scss">
.line-wrapper,
.workorder-sheet,
.fault-sheet {
  height: 292px;
  position: relative;
  .sheet {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
  }
}
.workorder-sheet {
  height: 300px;
}
.fault-sheet {
  height: 40px;
  width: 100%;
  margin-right: 16px;
}
</style>
